import { useState, useEffect } from "react";
import {
  isReadOnlyUser,
  isManagementUser,
} from "../../../shared/utility";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import ReceivingSubTable1 from "./subtables/ReceivingSubtable1";
import NumberInputText from "../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../hooks/useCellSaveState";

export default function ReceivingItemsTableComponent(props) {
  const {
    loading,
    values,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    onUpdateGroupQuantities,
    updateReceivingItem,
    closed,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
    reprintReceivingItem,
  } = props;

  // optional pagination props from parent
  const { onFetchPage, rowsPerPage: rowsPerPageProp } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedRows, setExpandedRows] = useState(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(rowsPerPageProp || 15);
  const { getSaveStatus, saveCell } = useCellSaveState();
  const selectedItemCount = Array.isArray(selectedItems)
    ? selectedItems.length
    : 0;

  const parentCellKey = (item, field) => `parent:${item.id}:${field}`;

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const rowExpansionTemplate = (data) => {
    return (
      <ReceivingSubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        closed={closed}
        onUpdateReceivingItem={(payload, parentRow) =>
          updateReceivingItem({
            ...payload,
            parentRow,
            is_paginated: isPaginated,
            page: currentPage,
          })
        }
      />
    );
  };

  // Determine whether items is paginated (Laravel LengthAwarePaginator shape)
  const isPaginated =
    values &&
    values.items &&
    typeof values.items === "object" &&
    !Array.isArray(values.items) &&
    Object.prototype.hasOwnProperty.call(values.items, "data") &&
    Object.prototype.hasOwnProperty.call(values.items, "current_page");

  const items = isPaginated
    ? values.items.data
    : Array.isArray(values.items)
      ? values.items
      : [];
  const perPage = isPaginated ? values.items.per_page : rowsPerPageProp || rows;
  const totalRecords = isPaginated ? values.items.total : items.length;

  // keep rows/perPage in sync when values change
  useEffect(() => {
    if (perPage && perPage !== rows) {
      setRows(perPage);
    }
    // rows intentionally excluded to avoid loop; setRows is safe here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  // sync `first` offset from incoming paginator (so UI stays in sync when parent updates)
  const itemsPagin = values && values.items ? values.items : null;
  useEffect(() => {
    if (isPaginated && itemsPagin) {
      const newFirst =
        (itemsPagin.current_page - 1) * (itemsPagin.per_page || rows);
      setFirst(newFirst);
    } else {
      setFirst(0);
    }
    // rows intentionally excluded to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginated, itemsPagin]);

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br /> {item.plu}
      </p>
    );
  };

  const bodyParentTotalWeight = (item) => {
    const canEdit =
      isManagement &&
      !isReadOnly &&
      !closed &&
      typeof onUpdateGroupQuantities === "function";

    return (
      <NumberInputText
        value={item.total_weight}
        aria-label="Total weight"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEdit}
        saveStatus={getSaveStatus(parentCellKey(item, "total_weight"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "total_weight"), () =>
            updateGroupQuantity(item, "total_weight", value),
          )
        }
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
        suffix={` ${item.weight_selling_unit_of_measure_name}`}
      />
    );
  };

  const bodyParentItemWeight = (item) => {
    const canEdit =
      isManagement &&
      !isReadOnly &&
      !closed &&
      typeof onUpdateGroupQuantities === "function";

    return (
      <NumberInputText
        value={item.net_weight}
        aria-label="Unit weight"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEdit}
        saveStatus={getSaveStatus(parentCellKey(item, "unit_weight"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "unit_weight"), () =>
            updateGroupQuantity(item, "unit_weight", value),
          )
        }
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
        suffix={` ${item.weight_selling_unit_of_measure_name}`}
      />
    );
  };

  const bodyParentPieceCount = (item) => {
    const canEdit =
      isManagement &&
      !isReadOnly &&
      !closed &&
      typeof onUpdateGroupQuantities === "function";

    return (
      <NumberInputText
        value={item.total_pieces}
        aria-label="Total pieces"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEdit}
        saveStatus={getSaveStatus(parentCellKey(item, "total_pieces"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "total_pieces"), () =>
            updateGroupQuantity(item, "total_pieces", value),
          )
        }
        minFractionDigits={0}
        maxFractionDigits={0}
        min={0}
        suffix=" pieces"
      />
    );
  };

  const bodyParentUnitCost = (item) => {
    const canEdit =
      isManagement &&
      !isReadOnly &&
      !closed &&
      typeof onUpdateUnitCostForReceivingItem === "function";

    return (
      <NumberInputText
        value={item.unit_cost}
        aria-label="Unit cost"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEdit}
        saveStatus={getSaveStatus(parentCellKey(item, "unit_cost"))}
        onCommit={(unitCost) =>
          saveCell(parentCellKey(item, "unit_cost"), () =>
            updateParentValue(item, () =>
              onUpdateUnitCostForReceivingItem({
                unique_id: item.unique_id,
                plu: item.plu,
                lot_number: item.lot_number,
                unit_cost: unitCost,
                warehouse_location_id: item.warehouse_location_id,
                is_paginated: isPaginated,
                page: currentPage,
              }),
            ),
          )
        }
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentTotalCost = (item) => {
    const canEdit =
      isManagement &&
      !isReadOnly &&
      !closed &&
      typeof onUpdateTotalCostForReceivingItem === "function";

    return (
      <NumberInputText
        value={item.total_cost}
        aria-label="Total cost"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEdit}
        saveStatus={getSaveStatus(parentCellKey(item, "total_cost"))}
        onCommit={(totalCost) =>
          saveCell(parentCellKey(item, "total_cost"), () =>
            updateParentValue(item, () =>
              onUpdateTotalCostForReceivingItem({
                unique_id: item.unique_id,
                plu: item.plu,
                lot_number: item.lot_number,
                total_cost: totalCost,
                warehouse_location_id: item.warehouse_location_id,
                total_weight: item.total_weight,
                total_pieces: item.total_pieces,
                selling_unit_of_measure: item.selling_unit_of_measure,
              }),
            ),
          )
        }
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentLocation = (item) => {
    return (
      <p>
        {item.warehouse_name} <br />
        {item.warehouse_location_name}
      </p>
    );
  };

  const cartonCountBody = (item) => {
    const canEdit =
      isManagement &&
      !isReadOnly &&
      !closed &&
      typeof onUpdateGroupQuantities === "function";

    return (
      <NumberInputText
        value={item.carton_count}
        aria-label="Carton count"
        className="editable-table-number"
        inputClassName="editable-table-input"
        disabled={!canEdit}
        saveStatus={getSaveStatus(parentCellKey(item, "carton_count"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "carton_count"), () =>
            updateGroupQuantity(item, "carton_count", value),
          )
        }
        minFractionDigits={0}
        maxFractionDigits={0}
        min={1}
        suffix=" cartons"
      />
    );
  };

  const updateParentValue = async (item, update) => {
    const wasExpanded = Boolean(expandedRows?.[item.id]);
    const callResult = await update();

    if (!callResult?.success || !wasExpanded) {
      return callResult;
    }

    setExpandedRows((currentExpandedRows) => {
      const nextExpandedRows = { ...currentExpandedRows };
      delete nextExpandedRows[item.id];

      return Object.keys(nextExpandedRows).length > 0
        ? nextExpandedRows
        : null;
    });
    await fetchRowDetails(item);
    return callResult;
  };

  const updateGroupQuantity = (item, field, value) => {
    return updateParentValue(item, () =>
      onUpdateGroupQuantities({
        product_id: item.product_id,
        lot_number: item.lot_number,
        warehouse_location_id: item.warehouse_location_id,
        field,
        value,
      }),
    );
  };

  const isReadOnly = isReadOnlyUser();
  const isManagement = isManagementUser();

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-4">
          <div className="w-full text-right">
            {!isReadOnly && (
              <button
                onClick={askReturnSelectedItems}
                className="ml-2 rounded-md bg-red-600 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Delete{selectedItemCount > 0 ? ` (${selectedItemCount})` : ""}
              </button>
            )}
          </div>
          {loading ? (
            <div>
              <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                <div className="animate-pulse flex space-x-4">Loading</div>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                cellClassName="p-2 border-1-bottom-gray text-sm"
              >
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  header="Vendor Sku"
                  field="vendor_sku"
                  style={{ width: "8%" }}
                />
                <Column
                  field="lot_number"
                  header="Lot Number"
                  style={{ width: "15%" }}
                />
                <Column
                  header="Carton Count"
                  body={cartonCountBody}
                  style={{ width: "10%" }}
                />
                <Column
                  header="Unit Wt"
                  body={bodyParentItemWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Wt"
                  body={bodyParentTotalWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentPieceCount}
                  style={{ width: "8%" }}
                />
                {isManagement ? (
                  <Column
                    header="Unit Cost"
                    field="unit_cost"
                    body={bodyParentUnitCost}
                    style={{ width: "8%" }}
                  />
                ) : null}
                {isManagement ? (
                  <Column
                    header="Total Cost"
                    field="total_cost"
                    body={bodyParentTotalCost}
                    style={{ width: "8%" }}
                  />
                ) : null}

                <Column
                  body={bodyParentLocation}
                  header="Location"
                  style={{ width: "15%" }}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              {isPaginated ? (
                <div className="w-full">
                  <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={(event) => {
                      const nextPage = event.page + 1;
                      setFirst(event.first);
                      setRows(event.rows);
                      setCurrentPage(nextPage);
                      if (onFetchPage) onFetchPage(nextPage);
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
